import { ApolloClient } from "apollo-client";

import { from, Observable } from "apollo-link";
import { createHttpLink } from "apollo-link-http";
import { setContext } from "apollo-link-context";
import { onError } from "apollo-link-error";

import uuid from "uuid/v4";

import { InMemoryCache, defaultDataIdFromObject } from "apollo-cache-inmemory";

import fetch from "isomorphic-fetch";

import { IntrospectionFragmentMatcher } from "apollo-cache-inmemory";
import introspectionQueryResultData from "./fragmentTypes.json";

import { getErrors } from "./errors";

import { getAppVersion } from "src/sdk";

const fragmentMatcher = new IntrospectionFragmentMatcher({
  introspectionQueryResultData,
});

const url = process.env.GATSBY_GQL_API_URL || process.env.GATSBY_API_URL || "";

const createHttpLinkWithCustomFetch = ({ setToken }) => {
  const customFetch = async (uri, options) => {
    const response = await fetch(uri, options);

    const token = response.headers.get("token");

    if (token) {
      setToken(token);
    }

    return response;
  };

  return createHttpLink({ uri: `${url}/api/graphql`, fetch: customFetch });
};

const cache = new InMemoryCache({
  fragmentMatcher,
  dataIdFromObject: object => {
    switch (object.__typename) {
      default:
        return defaultDataIdFromObject(object); // fall back to default handling
    }
  },
});

const authLink = setContext((_, { headers }) => {
  // get the authentication token from local storage if it exists
  const token = localStorage.getItem("token");
  const topLevelAccountId = localStorage.getItem("topLevelAccountId");
  // return the headers to the context so httpLink can read them
  return {
    headers: {
      ...headers,
      topLevelAccountId: topLevelAccountId ? `${topLevelAccountId}` : "",
      appVersion: getAppVersion(),
      authorization: token ? `${token}` : "",
    },
  };
});

const authenticationErrorLink = ({
  authenticationError,
  setAuthenticationError,
}) => {
  const errorLink = onError(({ forward, operation, ...error }) => {
    const { authenticationErrors } = getErrors(error);

    if (
      !authenticationError &&
      authenticationErrors &&
      authenticationErrors.length
    ) {
      const newAuthenticationError = authenticationErrors[0];

      return new Observable(observer => {
        setAuthenticationError({
          ...newAuthenticationError,
          id: uuid(),
          resolve: () => {
            // There seems to be a race condition if this is missing.
            const subscriber = {
              next: observer.next.bind(observer),
              error: observer.error.bind(observer),
              complete: observer.complete.bind(observer),
            };

            // Retry last failed request
            if (operation.getContext().retryOnAuthentication) {
              forward(operation).subscribe(subscriber);
            }

            setAuthenticationError(null);
          },
        });
      });
    }
  });

  return errorLink;
};

export const createClient = ({
  authenticationError,
  setAuthenticationError,
  setToken,
}) => {
  // Including this twice for the rare occasion where the next query causes
  // another authentication error.
  const authErrorLink = authenticationErrorLink({
    authenticationError,
    setAuthenticationError,
  });

  return new ApolloClient({
    link: from([
      authErrorLink,
      authLink,
      createHttpLinkWithCustomFetch({ setToken }),
    ]),
    cache,
  });
};



// WEBPACK FOOTER //
// ./src/graphql/client.js